import { useEffect, useMemo } from 'react'
import { DateTime } from 'luxon'
import { HashLink } from 'react-router-hash-link'

import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  fetchSubscriptions,
  selectDaysUntilSuspension,
  selectHeardProductSubscription,
} from '@/reducers/subscription.slice'
import {
  getCurrentUser,
  isCancelling,
  selectIsInUnservicedFreeTrial,
} from '@/selectors/user.selectors'
import { Alert } from '@/ui'
import { SignBaaModal } from '../SignBaaModal'
import { FreeTrialHomepageBanner } from '@/components/platform-banners/FreeTrialHomepageBanner'
import { TermsOfServiceModal } from '../TermsOfServiceModal'
import { TermsOfServiceModalV2 } from '../TermsOfServiceModalV2'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { TosVersion } from '@/constants/businessConstants'
import { AccountSuspendedBanner } from '@/features/accounts/AccountSuspendedBanner'
import { BannerRouter } from '@/components/routes/Router'
import {
  TOS_MODAL,
  useBaseRouterModalToShow,
} from '@/components/platform-banners/banner.selectors'
import { GustoTosModal } from '@/components/platform-banners/GustoTosModal'
import { TosUpdateBanner } from '@/components/platform-banners/TosUpdateBanner'
import { PlaidConnectReminderModal } from '@/features/banking/plaid-connect-reminder'

const TosModalMap: Record<TosVersion, typeof TermsOfServiceModal> = {
  [TosVersion.tos_02_2022]: TermsOfServiceModal,
  [TosVersion.tos_01_2024]: TermsOfServiceModalV2,
}

export const BaseRouterBanners = () => {
  const dispatch = useAppDispatch()
  const currentUser = useReselector(getCurrentUser)
  const primarySubscription = useReselector(selectHeardProductSubscription)
  const daysUntilSuspension = useReselector(selectDaysUntilSuspension)
  const showCancellingBanner = useReselector(isCancelling)
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)
  const { baseModalToShow, dismissModal } = useBaseRouterModalToShow()

  const bannerModal = useMemo(() => {
    if (!currentUser?.id) {
      return null
    }

    switch (baseModalToShow) {
      case TOS_MODAL.HEARD_TOS: {
        const TosModal = TosModalMap[currentUser.currentTosVersion]
        return TosModal ? <TosModal currentUserId={currentUser.id} /> : null
      }
      case TOS_MODAL.BAA_MODAL:
        return (
          <SignBaaModal
            dismissModal={() => dismissModal(TOS_MODAL.BAA_MODAL)}
          />
        )
      case TOS_MODAL.GUSTO_TOS:
        return (
          <GustoTosModal
            dismissModal={() => dismissModal(TOS_MODAL.GUSTO_TOS)}
          />
        )
      case TOS_MODAL.PLAID_CONNECT_REMINDER:
        return (
          <PlaidConnectReminderModal
            onDismiss={() => dismissModal(TOS_MODAL.PLAID_CONNECT_REMINDER)}
          />
        )
      default:
        return null
    }
  }, [
    baseModalToShow,
    currentUser?.currentTosVersion,
    currentUser?.id,
    dismissModal,
  ])

  useEffect(() => {
    // Subscriptions drive customer suspension/cancellation banners; a Stytch-only
    // admin has no customer session, so skip it (would only 401 in the admin app).
    if (!currentUser?.admin) {
      dispatch(fetchSubscriptions())
    }
  }, [dispatch, currentUser?.admin])

  if (!currentUser) {
    return null
  }

  if (showCancellingBanner) {
    return (
      <>
        <TosUpdateBanner />
        <Alert variant="error">
          <div className="font-semibold">Canceled Plan</div>
          <div className="mt-1">
            Your Heard subscription plan is canceled. You have full access to
            Heard until your billing cycle ends on{' '}
            {primarySubscription?.nextBillingDate &&
              DateTime.fromSeconds(
                primarySubscription.nextBillingDate
              ).toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}
            .
            <br />
            <HashLink to="/accounts#reactivation" className="underline">
              Reactivate your account.
            </HashLink>
          </div>
        </Alert>
        <BannerRouter />
      </>
    )
  }

  if (isInUnservicedFreeTrial) {
    return (
      <>
        <TosUpdateBanner />
        <FreeTrialHomepageBanner />
      </>
    )
  }

  return (
    <>
      <TosUpdateBanner />
      {bannerModal}
      {daysUntilSuspension !== -1 && (
        <AccountSuspendedBanner daysUntilSuspension={daysUntilSuspension} />
      )}
      <BannerRouter />
    </>
  )
}
